import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import { useAdminWebSocketStore } from "../../../stores/websocketStore";

const MAX_LOG_ENTRIES = 200;

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  logBox: {
    marginTop: theme.spacing(2),
    maxHeight: 360,
    overflowY: 'auto',
    backgroundColor: '#1a1a2e',
    color: '#e0e0e0',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.78rem',
    lineHeight: 1.6,
    padding: theme.spacing(1.5),
    borderRadius: 4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  logEntry: {
    padding: '1px 0',
  },
  logEmpty: {
    color: '#888',
    fontStyle: 'italic',
  },
  status: {
    marginTop: theme.spacing(1),
  },
}));

export default function CapacityPlannerSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);
  const logEndRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [config, setConfig] = useState({
    playersPerServer: 10,
    spareBuffer: 0,
    tickIntervalSeconds: 60,
    cooldownSeconds: 300,
    dryRun: true,
    maxDesiredPerRegion: 10,
    leaderTtlSeconds: 90,
    eagerStartEnabled: true,
    eagerStartMinPlayers: 5,
    eagerStartRequireRising: true,
    eagerStartScaleDownSeconds: 300,
  });

  const [logEntries, setLogEntries] = useState([]);

  // WebSocket subscription for narrative stream
  const { subscribe } = useAdminWebSocketStore();

  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (event?.message?.messageId === 'capacity-planner-narrative') {
        const entries = event.message.entries || [];
        if (entries.length) {
          setLogEntries((prev) => {
            const next = [...prev, ...entries];
            return next.length > MAX_LOG_ENTRIES
              ? next.slice(next.length - MAX_LOG_ENTRIES)
              : next;
          });
        }
      }
    });
    return unsubscribe;
  }, [subscribe]);

  // Auto-scroll log
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logEntries]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/capacity-planner/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load capacity planner settings');
      if (!isMountedRef.current) return;
      setEnabled(data.enabled === true);
      if (data.config && typeof data.config === 'object') {
        setConfig((prev) => ({ ...prev, ...data.config }));
      }
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (payload) => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/capacity-planner/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save capacity planner settings');
      if (!isMountedRef.current) return;
      if (typeof data.enabled === 'boolean') setEnabled(data.enabled);
      if (data.config) setConfig((prev) => ({ ...prev, ...data.config }));
      setSuccess('Settings saved.');
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, userId]);

  const handleToggle = useCallback(() => {
    save({ enabled: !enabled });
  }, [enabled, save]);

  const handleConfigSave = useCallback(() => {
    save({ config });
  }, [config, save]);

  const updateConfigField = useCallback((field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Capacity Planner</Typography>
          <Typography variant="body2" color="textSecondary">
            Automatically adjusts ASG desired capacity based on queue depth.
            Streams a real-time narrative of decisions to this panel.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {/* Enable / Disable toggle */}
        <Box className={classes.row}>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Status: {enabled ? 'ENABLED' : 'DISABLED'}
          </Typography>
          <Switch
            checked={enabled}
            onChange={handleToggle}
            disabled={loading || saving || !userId}
            color="primary"
          />
          <Button variant="outlined" onClick={load} disabled={loading || saving || !userId}>
            Refresh
          </Button>
        </Box>

        {/* Config fields */}
        <Box className={classes.configGrid}>
          <TextField
            label="Players per server"
            type="number"
            variant="outlined"
            size="small"
            value={config.playersPerServer}
            onChange={(e) => updateConfigField('playersPerServer', Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Spare buffer"
            type="number"
            variant="outlined"
            size="small"
            value={config.spareBuffer}
            onChange={(e) => updateConfigField('spareBuffer', Math.max(0, Number(e.target.value) || 0))}
            inputProps={{ min: 0 }}
            helperText="Added after floor(queueSize / playersPerServer)"
          />
          <TextField
            label="Tick interval (s)"
            type="number"
            variant="outlined"
            size="small"
            value={config.tickIntervalSeconds}
            onChange={(e) => updateConfigField('tickIntervalSeconds', Math.max(10, Number(e.target.value) || 60))}
            inputProps={{ min: 10 }}
          />
          <TextField
            label="Scale-down cooldown (s)"
            type="number"
            variant="outlined"
            size="small"
            value={config.cooldownSeconds}
            onChange={(e) => updateConfigField('cooldownSeconds', Math.max(0, Number(e.target.value) || 0))}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Max desired / region"
            type="number"
            variant="outlined"
            size="small"
            value={config.maxDesiredPerRegion}
            onChange={(e) => updateConfigField('maxDesiredPerRegion', Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Leader lease TTL (s)"
            type="number"
            variant="outlined"
            size="small"
            value={config.leaderTtlSeconds}
            onChange={(e) => updateConfigField('leaderTtlSeconds', Math.max(15, Number(e.target.value) || 90))}
            inputProps={{ min: 15 }}
            helperText="Prevents multiple edge replicas from scaling at once"
          />
        </Box>

        {/* Eager remainder starter */}
        <Typography variant="subtitle2" style={{ marginTop: 16, fontWeight: 600 }}>
          Eager Remainder Starter
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Pre-warms one extra server for a partial batch (before it fills up) so players wait less,
          applied to every batch remainder — e.g. 25 players with 10/server can target 3 servers
          (2 confident + 1 eager) instead of 2.
        </Typography>
        <Box className={classes.row}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Eager start enabled:
          </Typography>
          <Switch
            checked={config.eagerStartEnabled === true}
            onChange={() => updateConfigField('eagerStartEnabled', !config.eagerStartEnabled)}
            color="primary"
          />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Require rising trend:
          </Typography>
          <Switch
            checked={config.eagerStartRequireRising === true}
            onChange={() => updateConfigField('eagerStartRequireRising', !config.eagerStartRequireRising)}
            color="primary"
          />
        </Box>
        <Box className={classes.configGrid}>
          <TextField
            label="Eager min players (remainder)"
            type="number"
            variant="outlined"
            size="small"
            value={config.eagerStartMinPlayers}
            onChange={(e) => updateConfigField('eagerStartMinPlayers', Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1 }}
            helperText="Batch remainder needed to pre-warm one server"
          />
          <TextField
            label="Eager scale-down delay (s)"
            type="number"
            variant="outlined"
            size="small"
            value={config.eagerStartScaleDownSeconds}
            onChange={(e) => updateConfigField('eagerStartScaleDownSeconds', Math.max(0, Number(e.target.value) || 0))}
            inputProps={{ min: 0 }}
            helperText="How long the remainder must stay below threshold before removing the eager server"
          />
        </Box>

        <Box className={classes.row}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Dry run (narrate only, no actual scaling):
          </Typography>
          <Switch
            checked={config.dryRun === true}
            onChange={() => updateConfigField('dryRun', !config.dryRun)}
            color="secondary"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfigSave}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : 'Save Config'}
          </Button>
        </Box>

        {/* Status messages */}
        {loading && (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading...
          </Typography>
        )}
        {error && (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        )}
        {success && (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        )}

        {/* Narrative log */}
        <Typography variant="subtitle2" style={{ marginTop: 16, fontWeight: 600 }}>
          Live Narrative
        </Typography>
        <Box className={classes.logBox}>
          {logEntries.length === 0 ? (
            <span className={classes.logEmpty}>
              Waiting for capacity planner events...
              {!enabled && ' (planner is currently disabled)'}
            </span>
          ) : (
            logEntries.map((entry, i) => (
              <div key={i} className={classes.logEntry}>{entry}</div>
            ))
          )}
          <div ref={logEndRef} />
        </Box>

        <Box className={classes.row} style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setLogEntries([])}
            disabled={logEntries.length === 0}
          >
            Clear Log
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
}
